import React, { Component, ReactNode, CSSProperties } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import RadioGroupContext from "./RadioGroupContext";

import { styles } from "../../utils/styles";
import { MenuItemEventHandler, TriggerEvent } from "../../types";

/**
 * @description RadioButtonItem 에 사용되는 Property type 정의
 * @interface ItemProps
 * @extends {StyleProps}
 * @extends {InternalProps}
 */
//export interface ItemProps extends StyleProps, InternalProps {
export interface ItemProps {
  /**
   * @description 체크가 된 상태일떄 출력되는 아이콘. SVG 정의 문자열을 입력.
   */
  //onIcon: string;
  onIcon: any;
  /**
   * @description 단축키 정의
   */
  shortcutKey: string;
  /**
   * @description children elements
   */
  children: ReactNode;
  /**
   * @description 저장 데이터(필요시 활용)
   */
  data?: object;
  /**
   * @description Radio button 값
   */
  value: string;
  /**
   * @description RadioGroupItem과 공유할 데이터 context
   */
  context: any;
  /**
   * @description 현재 item 사용여부 표시
   */
  disabled: boolean | ((args: MenuItemEventHandler) => boolean);
  /**
   * @description
   */

  nativeEvent: object;
  /**
   * @description
   */

  propsFromTrigger: object;
  /**
   * @description style
   */

  style: CSSProperties;
  /**
   * @description 컴포넌트에 추가할 Classname 정의
   */
  className: string;
  /**
   * Click 이벤트 발생시 호출되는 이벤트 핸들러
   */
  onClick: (args: React.MouseEvent) => any;
}

/// dummy function 정의
const noop = () => {};

/**
 *
 * @description RadioButton 과 같은 기능을 하는 ContextMenu Item UI 컴포넌트 정의
 * @class RadioButtonComponent
 * @extends {Component<ItemProps>}
 */
class RadioButtonComponent extends Component<ItemProps> {
  static propTypes = {
    //onIcon: PropTypes.string,
    onIcon: PropTypes.any,
    shortcutKey: PropTypes.string,
    children: PropTypes.node.isRequired,
    data: PropTypes.object,
    value: PropTypes.string,
    context: PropTypes.any,
    disabled: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    onClick: PropTypes.func,
    nativeEvent: PropTypes.object,
    propsFromTrigger: PropTypes.object,
    className: PropTypes.string,
    style: PropTypes.object
  };

  static defaultProps = {
    onIcon: "",
    shortcutKey: "",
    value: "",
    context: null,
    disabled: false,
    onClick: noop
  };

  isDisabled: boolean; // 버튼 사용 여부 표시

  constructor(props: ItemProps) {
    super(props);
    const { disabled, nativeEvent, propsFromTrigger, data } = this.props;

    this.isDisabled =
      typeof disabled === "function"
        ? disabled({
            event: nativeEvent as TriggerEvent,
            props: { ...propsFromTrigger, ...data }
          })
        : disabled;
  }

  /**
   * @description 마우스 클릭 이벤트 발생시 처리 핸들러. 현재 선택된 radio 값으로 업데이트 수행
   *
   * @memberof RadioButtonComponent
   */
  handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.isDisabled ? e.stopPropagation() : this.props.onClick(e);
    const { context, value } = this.props;

    if (context.selectedValue !== value) {
      context.updateValue("selectedValue", value);
    }
  };

  /**
   * @description Radio icon 렌더링
   *
   * @returns
   * @memberof RadioButtonComponent
   */
  renderIcon() {
    const { context, value, onIcon } = this.props;

    if (context.selectedValue === value) {
      return onIcon;
    }
    return "";
  }

  /**
   * @description 컴포넌트 UI 렌더링
   *
   * @returns
   * @memberof RadioButtonComponent
   */
  render() {
    const { className, style, children, shortcutKey } = this.props;

    const cssClasses = cx(styles.item, className, {
      [`${styles.itemDisabled}`]: this.isDisabled
    });

    return (
      <div
        className={cssClasses}
        style={style}
        onClick={this.handleClick}
        role="presentation"
      >
        <div className={styles.leftAlign}>
          <div className={styles.itemIcon}>{this.renderIcon()}</div>

          <div className={styles.itemContent}>{children}</div>
        </div>
        <div className={styles.rightAlign}>
          <div className={styles.itemShortcutKey}>{shortcutKey}</div>
        </div>
      </div>
    );
  }
}

/**
 * @description RadioButtonComponent를 Context기능으로 wrapping 하는 함수.
 * RadioGroup,button 구현을위해 React에서 제공하는 Provider/Consumer(Context)를
 * 활용하였으나, 추후 해당 기능 수정가능성이 있음으로, 쉬운 변경을 위해 아래와 같이
 * 버튼 구현부와 Context 활용부를 분리
 * @function RadioButtonItem
 * @param {RadioButtonComponent} Component
 * @returns
 */
const RadioButtonItem = (Component: any) => {
  return function RadioButtonItem(props: any) {
    return (
      <RadioGroupContext.Consumer>
        {(context: any) => {
          return <RadioButtonComponent {...props} context={context} />;
        }}
      </RadioGroupContext.Consumer>
    );
  };
};

export default RadioButtonItem(RadioButtonComponent);
